/**
 * Systemmeldinger i prosjektkanaler (VIB-3026). Hendelser fra
 * `project_events` vises som meldinger fra «Revehiet». Bare lesing: ingen
 * nye rader skrives, og systemmeldinger teller ikke som uleste.
 */
import { formatHours, parseTaskDescription } from "@/lib/format";
import { PHASE_LABELS } from "@/lib/offer-types";
import type { ChannelMessage } from "./messages";

export const CHANNEL_EVENT_TYPES = ["phase_changed", "task_completed", "task_approved", "time_entry_created"] as const;
export type ChannelEventType = (typeof CHANNEL_EVENT_TYPES)[number];

export interface ChannelEventRow {
  id: string;
  project_id: string;
  user_id: string | null;
  event_type: string;
  payload: Record<string, unknown> | null;
  created_at: string;
  user?: { full_name: string } | null;
}

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

function phaseLabel(phase: string): string {
  return PHASE_LABELS[phase as keyof typeof PHASE_LABELS] ?? phase;
}

/** Oppgavenavnet fra «Prosjekt - Fase - Oppgave»; siste ledd hvis fasen er ukjent. */
function taskFromDescription(desc: string): string {
  const parsed = parseTaskDescription(desc).task;
  if (parsed) return parsed;
  const parts = desc.split(" - ");
  return parts[parts.length - 1]?.trim() ?? "";
}

/** Én hendelse som systemmelding, eller null hvis den mangler det vi trenger. */
export function eventToMessage(e: ChannelEventRow): ChannelMessage | null {
  const actor = e.user?.full_name ?? null;
  const p = e.payload ?? {};
  const base = {
    id: `event:${e.id}`,
    kind: "system" as const,
    authorId: e.user_id,
    authorName: "Revehiet",
    authorAvatarUrl: null,
    createdAt: e.created_at,
    mentionedUserIds: [],
    attachments: [],
    likedByUserIds: [],
  };
  const projectLink = { kind: "Prosjekt", label: "Prosjektkortet", href: `/prosjekter/${e.project_id}` };
  const budgetLink = { kind: "Budsjett", label: "Tilbud og budsjett", href: `/prosjekter/${e.project_id}/budsjett` };

  switch (e.event_type) {
    case "phase_changed": {
      const phase = text(p.new_value);
      if (!phase) return null;
      const label = phaseLabel(phase);
      return {
        ...base,
        body: actor ? `${actor} flyttet prosjektet til ${label}.` : `Prosjektet er flyttet til ${label}.`,
        link: projectLink,
      };
    }
    case "task_completed": {
      const task = text(p.task_name);
      if (!task) return null;
      return { ...base, body: actor ? `${actor} markerte «${task}» som fullført.` : `«${task}» er fullført.`, link: budgetLink };
    }
    case "task_approved": {
      const task = text(p.task_name);
      if (!task) return null;
      return { ...base, body: actor ? `«${task}» er godkjent av ${actor}.` : `«${task}» er godkjent.`, link: budgetLink };
    }
    case "time_entry_created": {
      const hours = typeof p.hours === "number" ? p.hours : Number(p.hours);
      const task = taskFromDescription(text(p.task_description));
      if (!Number.isFinite(hours) || hours <= 0 || !task) return null;
      const who = actor ?? "Noen";
      return { ...base, body: `${who} registrerte ${formatHours(hours)} på «${task}».`, link: budgetLink };
    }
    default:
      return null;
  }
}

export function eventsToMessages(events: ChannelEventRow[]): ChannelMessage[] {
  return events.map(eventToMessage).filter((m): m is ChannelMessage => m !== null);
}
